import { bracketMatching, foldGutter, syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { Compartment, EditorState } from '@codemirror/state';
import { Decoration, type DecorationSet, EditorView, lineNumbers, ViewPlugin, WidgetType } from '@codemirror/view';
import { memo, useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { themeStore } from '~/lib/stores/theme';
import { classNames } from '~/utils/classNames';
import { createScopedLogger } from '~/utils/logger';
import { getTheme, reconfigureTheme } from './cm-theme';
import { getLanguage } from './languages';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';

const logger = createScopedLogger('ReadOnlyCodeMirror');

/**
 * Above this longest-line length we render WITHOUT syntax highlighting.
 * Highlighting + re-measuring a multi-thousand-char single line on every
 * streaming append locks the main thread hard enough to trip Chrome's "Page
 * Unresponsive" dialog (observed on chat 93d832ed, whose pages were written as
 * single 1.5–5K-char lines). write_file now auto-formats such files, but this
 * guard keeps the viewer safe for any mega-line from any source (old chats,
 * minified/vendored files, pasted content). Plain text still renders fine.
 */
const MAX_HIGHLIGHT_LINE_LENGTH = 2_000;

function hasMegaLine(content: string): boolean {
  let lineStart = 0;

  for (let i = 0; i <= content.length; i++) {
    if (i === content.length || content[i] === '\n') {
      if (i - lineStart > MAX_HIGHLIGHT_LINE_LENGTH) {
        return true;
      }

      lineStart = i + 1;
    }
  }

  return false;
}

interface Props {
  /** Content to display. During streaming this grows; updates are applied to the tail only. */
  content: string;
  /** File path — drives syntax highlighting via extension. */
  filePath: string;
  /** Show a blinking block cursor at the end of the content (the write head). */
  showCursor?: boolean;
  /** Keep the view scrolled to the end as content grows. */
  autoScroll?: boolean;
  className?: string;
}

/** A blinking block cursor rendered at the streaming write head. */
class CursorWidget extends WidgetType {
  toDOM() {
    const span = document.createElement('span');
    span.className = 'cm-stream-cursor';

    return span;
  }

  eq() {
    return true;
  }
}

/** Compartment-driven plugin: when enabled, draws a single cursor widget at doc end. */
function cursorPlugin(enabledRef: { current: boolean }) {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      view: EditorView;

      constructor(view: EditorView) {
        this.view = view;
        this.decorations = this.build(view);
      }

      update() {
        // Rebuilt on every view update so the widget tracks the growing doc end.
        this.decorations = this.build(this.view);
      }

      build(view: EditorView): DecorationSet {
        this.view = view;

        if (!enabledRef.current) {
          return Decoration.none;
        }

        const end = view.state.doc.length;

        return Decoration.set([Decoration.widget({ widget: new CursorWidget(), side: 1 }).range(end)]);
      }
    },
    { decorations: (v) => v.decorations },
  );
}

export const ReadOnlyCodeMirror = memo(
  ({ content, filePath, showCursor = false, autoScroll = false, className }: Props) => {
    const theme = useStore(themeStore);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const viewRef = useRef<EditorView>();
    const languageCompartment = useRef(new Compartment());
    // Long lines scroll sideways inside an already narrow pane on phones — wrap them there.
    const wrapCompartment = useRef(new Compartment());
    const wrapLines = useIsBelow(BREAKPOINTS.md);
    const wrapLinesRef = useRef(wrapLines);
    const cursorEnabledRef = useRef(showCursor);
    const prevShowCursorRef = useRef(showCursor);
    const currentFileRef = useRef<string>('');
    // Once a mega-line is seen, highlighting stays OFF for this file — streaming
    // can grow a line past the threshold mid-way, and re-enabling would re-tokenise.
    const megaLineRef = useRef(false);

    cursorEnabledRef.current = showCursor;

    // Create the editor once.
    useEffect(() => {
      const view = new EditorView({
        parent: containerRef.current!,
        state: EditorState.create({
          doc: '',
          extensions: [
            EditorState.readOnly.of(true),
            EditorView.editable.of(false),
            getTheme(themeStore.get()),
            lineNumbers(),
            foldGutter(),
            bracketMatching(),
            syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
            languageCompartment.current.of([]),
            wrapCompartment.current.of(wrapLinesRef.current ? EditorView.lineWrapping : []),
            cursorPlugin(cursorEnabledRef),
          ],
        }),
      });

      viewRef.current = view;

      return () => {
        view.destroy();
        viewRef.current = undefined;
      };
    }, []);

    // Reconfigure theme when it changes.
    useEffect(() => {
      viewRef.current?.dispatch({ effects: reconfigureTheme(theme) });
    }, [theme]);

    useEffect(() => {
      wrapLinesRef.current = wrapLines;
      viewRef.current?.dispatch({
        effects: wrapCompartment.current.reconfigure(wrapLines ? EditorView.lineWrapping : []),
      });
    }, [wrapLines]);

    // Load the language extension when the file changes. The import is async, so a
    // rapid A→B switch can resolve out of order — bail if the file changed before
    // this resolves, and fall back to no highlighting (logged) if the pack fails.
    useEffect(() => {
      if (filePath === currentFileRef.current) {
        return;
      }

      currentFileRef.current = filePath;
      megaLineRef.current = hasMegaLine(content);

      const requested = filePath;

      // A mega-line file renders as plain text — skip loading the language pack
      // entirely so a growing single line never gets syntax-highlighted.
      if (megaLineRef.current) {
        logger.warn('File has a very long line; rendering as plain text to keep the editor responsive', requested);
        viewRef.current?.dispatch({ effects: languageCompartment.current.reconfigure([]) });

        return;
      }

      getLanguage(filePath)
        .then((support) => {
          if (currentFileRef.current !== requested || megaLineRef.current) {
            return; // a newer file won the race, or a mega-line appeared mid-stream
          }

          viewRef.current?.dispatch({
            effects: languageCompartment.current.reconfigure(support ? [support] : []),
          });
        })
        .catch((err) => {
          logger.warn('Failed to load syntax highlighting; rendering as plain text', requested, err);

          if (currentFileRef.current !== requested) {
            return;
          }

          viewRef.current?.dispatch({ effects: languageCompartment.current.reconfigure([]) });
        });
    }, [filePath]);

    // Apply content updates. Streaming appends, so replace only the changed tail
    // (common-prefix diff) to avoid re-tokenising the whole document each frame.
    useEffect(() => {
      const view = viewRef.current;

      if (!view) {
        return;
      }

      const cursorToggled = prevShowCursorRef.current !== showCursor;
      prevShowCursorRef.current = showCursor;

      const current = view.state.doc.toString();

      if (current === content) {
        // Content unchanged — only force a redraw if the cursor visibility flipped
        // (an empty transaction re-runs the cursor plugin's update()).
        if (cursorToggled) {
          view.dispatch({});
        }

        return;
      }

      let from = 0;
      const max = Math.min(current.length, content.length);

      while (from < max && current[from] === content[from]) {
        from++;
      }

      view.dispatch({
        changes: { from, to: current.length, insert: content.slice(from) },
      });

      // A streaming line can cross the threshold mid-write; the moment it does,
      // drop syntax highlighting so the growing mega-line stops re-tokenising.
      if (!megaLineRef.current && hasMegaLine(content)) {
        megaLineRef.current = true;
        logger.warn('Line grew very long mid-stream; disabling highlighting to keep the editor responsive');
        view.dispatch({ effects: languageCompartment.current.reconfigure([]) });
      }

      if (autoScroll) {
        view.dispatch({ effects: EditorView.scrollIntoView(view.state.doc.length) });
      }
    }, [content, showCursor, autoScroll]);

    return (
      <div className={classNames('relative h-full overflow-hidden', className)}>
        <div className="h-full overflow-hidden" ref={containerRef} />
      </div>
    );
  },
);

ReadOnlyCodeMirror.displayName = 'ReadOnlyCodeMirror';
